/**
 * EntryModelUsedInPopover Component
 *
 * Displays "X entry models" count for an Entry Model Category.
 * Click/tap opens a popover showing the list of entry model names.
 * Non-clickable when count is zero.
 */

import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
} from '@enfomentis/ui';

export interface EntryModelUsedInPopoverProps {
  /** Array of entry model names that use this category */
  entryModelNames: string[];
}

export const EntryModelUsedInPopover: React.FC<EntryModelUsedInPopoverProps> = ({
  entryModelNames,
}) => {
  const count = entryModelNames.length;

  // Format the display text with proper pluralization
  const displayText = `${count} ${count === 1 ? 'entry model' : 'entry models'}`;

  // Empty state - no items use this category
  if (count === 0) {
    return (
      <span className="text-sm text-text-tertiary">
        {displayText}
      </span>
    );
  }

  // Sort names alphabetically (case-insensitive) for display
  const sortedNames = [...entryModelNames].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );

  return (
    <Popover isNonModal placement="bottom">
      <PopoverTrigger>
        <Button
          variant="light"
          size="sm"
          className="text-sm text-text-secondary hover:text-text-primary min-w-0 px-1"
        >
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-3">
        <div className="w-72 space-y-1">
          <p className="text-xs font-medium text-text-secondary mb-2">
            Entry models using this category:
          </p>
          <ul className="space-y-1 max-h-48 overflow-y-auto min-w-0">
            {sortedNames.map((name, index) => (
              <li
                key={index}
                className="text-sm text-text-primary truncate w-full"
                title={name}
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};
